import { useCallback, useEffect } from "react";
import { rosterSocket } from "../../../../../../utils/Socket";
import { resumeAll } from "../../../../../wsRecovery/wsResume";
import { startHeartbeat } from "../../../../../wsRecovery/heartbeat";
import { getRosterDiff } from "../api/rosterApi";

export default function useRosterFileSocket({
  enabled,
  isLoggedIn,
  refetch
}) {
  const safeRefetch = useCallback(
    async (opts) => {
      try {
        await refetch(opts);
      } catch (err) {
        console.error("[RosterFile] refetch failed:", err);
      }
    },
    [refetch]
  );

  useEffect(() => {
    if (!isLoggedIn || !enabled) return;

    if (!rosterSocket.connected) {
      rosterSocket.connect();
      startHeartbeat(rosterSocket);
    }

    const onConnect = () => {
      rosterSocket.emit("joinRoster");
      resumeAll();
    };

    const onReconnect = async () => {
      rosterSocket.emit("joinRoster");
      try {
        const diff = await getRosterDiff();
        if (diff?.needRefresh) {
          await safeRefetch({ silent: true });
        }
      } catch (err) {
        console.error("[RosterFile] reconnect diff failed:", err);
      }
    };

    rosterSocket.on("connect", onConnect);
    rosterSocket.on("reconnect", onReconnect);

    return () => {
      rosterSocket.off("connect", onConnect);
      rosterSocket.off("reconnect", onReconnect);
    };
  }, [isLoggedIn, enabled, safeRefetch]);

  useEffect(() => {
    const handler = () => {
      if (!isLoggedIn || !enabled) return;
      if (!rosterSocket.connected) {
        rosterSocket.connect();
        startHeartbeat(rosterSocket);
      }
    };
    window.addEventListener("auth-ws-refreshed", handler);
    return () => window.removeEventListener("auth-ws-refreshed", handler);
  }, [isLoggedIn, enabled]);

  useEffect(() => {
    if (!isLoggedIn || !enabled) return;

    const resyncIfStale = async () => {
      try {
        const diff = await getRosterDiff();
        if (diff?.needRefresh) {
          await safeRefetch({ silent: true });
        }
      } catch (err) {
        console.error("[RosterFile] visibility/online diff failed:", err);
      }
    };

    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      resyncIfStale();
    };

    const onOnline = () => {
      resyncIfStale();
    };

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", onOnline);

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", onOnline);
    };
  }, [isLoggedIn, enabled, safeRefetch]);

  useEffect(() => {
    if (!isLoggedIn || !enabled) return;

    if (!rosterSocket.connected) {
      rosterSocket.connect();
      startHeartbeat(rosterSocket);
    }

    rosterSocket.emit("joinRoster");

    const onMissed = async () => {
      await safeRefetch({ silent: true });
    };

    const onUpdate = async () => {
      await safeRefetch({ silent: true });
    };

    rosterSocket.on("rosterMissed", onMissed);
    rosterSocket.on("rosterUpdate", onUpdate);

    return () => {
      rosterSocket.off("rosterMissed", onMissed);
      rosterSocket.off("rosterUpdate", onUpdate);
      // Do not emit leaveRoster: same singleton socket as useRosterSocket on the roster page;
      // leaving here would drop WS updates for the table when the file panel closes.
    };
  }, [isLoggedIn, enabled, safeRefetch]);
}

